import {
    BooleanField,
    Button,
    Datagrid,
    downloadCSV,
    FunctionField,
    NumberField,
    SimpleList,
    TextField,
    useCreate,
    useDataProvider,
    useTranslate,
    useRefresh,
} from 'react-admin';
import QRCode from 'qrcode';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import jsonexport from 'jsonexport/dist';
import { IconButton as MUIIconButton, Link as MUILink } from '@mui/material';
import QrCodeIcon from '@mui/icons-material/QrCode';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { InfiniteList } from '@components/InfiniteList';
import { useMediaQuery, Theme } from '@mui/material';
import { UploadButton } from '@components/UploadButton';
import { dataObject } from '@src/object.type';
import { getDay } from '@helpers/date.ext';
import env from '@src/environment';
import { entityResource as ent } from '@src/entityResource';

import MapIcon from '@mui/icons-material/Map';

const GMapButton = (lat: number, long: number) => {
    const url = `https://www.google.com/maps/search/?api=1&query=${lat},${long}`;
    return (
        <MUIIconButton
            component={MUILink}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={e => e.stopPropagation()}
        >
            <MapIcon />
        </MUIIconButton>
    );
};

const GenerateQrButton = ({ handlePress }: { handlePress: () => void }) => {
    return (
        <Button
            onClick={e => {
                e.stopPropagation();
                handlePress();
            }}
            startIcon={<QrCodeScannerIcon />}
        />
    );
};

const GenerateAllQrButton = ({
    label,
    handlePress,
}: {
    label: string;
    handlePress: () => void;
}) => {
    return (
        <Button
            onClick={handlePress}
            label={label}
            startIcon={<QrCodeIcon />}
        />
    );
};

export const StoreList = () => {
    const dp = useDataProvider();
    const [create] = useCreate();
    const refesh = useRefresh();
    const translate = useTranslate();
    const isSmall = useMediaQuery((theme: Theme) =>
        theme.breakpoints.down('sm')
    );

    const generateQr = async (store: dataObject) => {
        const { id, code } = store;
        return await QRCode.toDataURL(`${id}${code}`);
    };

    const handleGenerateAndDownloadQr = async (store: dataObject) => {
        const qr = await generateQr(store);
        const base64Data = qr.split(',')[1]; // Extract base64 data

        // Convert the base64 string to a Blob
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length)
            .fill(0)
            .map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: 'image/png' });

        // Save the Blob as a file
        saveAs(blob, `${store.id}${store.code}.png`);
    };

    const handleGenerateAndDownloadAllQr = async () => {
        const generateAndDownload = async () => {
            const { data } = await dp.getList(ent.STORE.UNGENERATED_QR, {});
            const zip = new JSZip();
            const folder = zip.folder('qrs');

            const promises = data.map(async store => {
                const qr = await generateQr(store);
                const base64Data = qr.split(',')[1];
                if (folder) {
                    folder.file(`${store.id}${store.code}.png`, base64Data, {
                        base64: true,
                    });

                    return {
                        ...store,
                        isGeneratedQr: true,
                    };
                }
            });

            const result = await Promise.all(promises);
            const content = await zip.generateAsync({ type: 'blob' });
            saveAs(content, 'qrcodes.zip');

            create(
                ent.STORE.UPLOAD_API,
                { data: result },
                {
                    onSuccess: () => {
                        refesh();
                    },
                }
            );
        };
        await generateAndDownload();
    };

    const exporter = (goods: dataObject[]) => {
        const storeForExport = goods.map(s => {
            const {
                code,
                name,
                address,
                latitude,
                longitude,
                phone,
                creditLimit,
                creditEffDtFrom,
                creditEffDtTo,
                creditBalance,
                visitDay,
                user,
            } = s;
            const u = user as unknown as { [key: string]: string | number };

            return {
                Code: code,
                Title: name,
                Description: address,
                Latitude: latitude,
                Longitude: longitude,
                ['Phone number']: phone,
                Credit: creditLimit,
                ['Credit Balance']: creditBalance,
                ['Credit Effective Date From']: creditEffDtFrom,
                ['Credit Effective Date To']: creditEffDtTo,
                ['Visit Day']: visitDay ? getDay(Number(visitDay)) : undefined,
                Sales: u?.name,
            };
        });

        jsonexport(
            storeForExport,
            {
                headers: [
                    'Code',
                    'Title',
                    'Description',
                    'Latitude',
                    'Longitude',
                    'Phone number',
                    'Credit',
                    'Credit Balance',
                    'Credit Effective Date From',
                    'Credit Effective Date To',
                    'Visit Day',
                    'Sales',
                ],
            },
            (err, csv) => {
                downloadCSV(
                    csv,
                    `data_toko_${new Date().toLocaleString(env.localFormat)}`
                );
            }
        );
    };

    return (
        <InfiniteList
            generateQrButton={
                <GenerateAllQrButton
                    handlePress={handleGenerateAndDownloadAllQr}
                    label={translate('resources.store.action.generateQr')}
                />
            }
            uploadButton={<UploadButton />}
            exporter={exporter}
        >
            {isSmall ? (
                <SimpleList
                    primaryText={record => record.name}
                    secondaryText={record => record.address}
                    tertiaryText={record => record.phone}
                />
            ) : (
                <Datagrid>
                    <TextField source="user.name" />
                    <FunctionField
                        source="visitDay"
                        render={r => getDay(r.visitDay)}
                    />
                    <TextField source="name" />
                    <TextField source="phone" />
                    <NumberField
                        source="creditLimit"
                        locales={env.localFormat}
                        textAlign="right"
                    />
                    <NumberField
                        source="debt"
                        locales={env.localFormat}
                        textAlign="right"
                    />
                    <NumberField
                        source="creditBalance"
                        locales={env.localFormat}
                        textAlign="right"
                    />
                    <BooleanField source="isGeneratedQr" />
                    <>
                        <FunctionField
                            render={r => GMapButton(r.latitude, r.longitude)}
                        />
                        <FunctionField
                            render={r => (
                                <GenerateQrButton
                                    handlePress={() =>
                                        handleGenerateAndDownloadQr(r)
                                    }
                                />
                            )}
                        />
                    </>
                </Datagrid>
            )}
        </InfiniteList>
    );
};
